import { ACTION_TYPES, type ActionType } from "@proclaw/shared";
import { Field, TextInput, Textarea, Select } from "./form-primitives";
import { ExpandableHighlightedTextarea } from "./expandable-highlighted-textarea";

const ACTION_LABELS: Record<ActionType, string> = {
  http_post: "HTTP POST to URL",
  send_email: "Send email",
  wait_for_email_reply: "Wait for email reply",
  post_to_channel: "Post to channel",
  mcp_tool: "Call MCP tool (any tool the agent has access to)",
};

/** Render the mcp_tool args bag for editing — object → pretty JSON, raw
 *  string left as-is (so partially-typed JSON isn't clobbered). */
function argsDisplay(config: Record<string, unknown>): string {
  const args = config.args;
  if (args == null) return "";
  if (typeof args === "string") return args;
  try {
    return JSON.stringify(args, null, 2);
  } catch {
    return "";
  }
}

export function ActionConfig({
  data,
  onChange,
  readOnly,
  variables,
}: {
  data: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  readOnly: boolean;
  variables?: { value: string; label: string; hint?: string }[];
}) {
  const actionType = (data.actionType as ActionType) ?? "http_post";
  const config = (data.config as Record<string, string>) ?? {};
  const update = (patch: Record<string, unknown>) => onChange({ ...data, ...patch });
  const updateConfig = (patch: Record<string, string>) =>
    update({ config: { ...config, ...patch } });
  const updateWaitConfig = (patch: Record<string, string>) => {
    const { deadlineAt: _legacyDeadline, ...currentConfig } = config;
    update({ config: { ...currentConfig, ...patch } });
  };

  return (
    <div className="space-y-4">
      <Field label="Label">
        <TextInput
          value={(data.label as string) ?? ""}
          onChange={(v) => update({ label: v })}
          placeholder="e.g. Add to Nurture List"
          readOnly={readOnly}
        />
      </Field>

      <Field label="Action type">
        <Select
          value={actionType}
          onChange={(v) =>
            update({
              actionType: v as ActionType,
              config:
                v === "wait_for_email_reply"
                  ? { waitDuration: "1", waitUnit: "days" }
                  : {},
            })
          }
          disabled={readOnly}
          options={ACTION_TYPES.map((t) => ({ value: t, label: ACTION_LABELS[t] }))}
        />
      </Field>

      {actionType === "wait_for_email_reply" && (
        <div className="grid grid-cols-[1fr_1.25fr] gap-2">
          <Field label="Wait for">
            <TextInput
              value={config.waitDuration ?? ""}
              onChange={(value) =>
                updateWaitConfig({
                  waitDuration: value.replace(/\D/g, ""),
                  waitUnit: config.waitUnit ?? "days",
                })
              }
              placeholder="1"
              readOnly={readOnly}
            />
          </Field>
          <Field label="Unit">
            <Select
              value={config.waitUnit ?? "days"}
              onChange={(value) => updateWaitConfig({ waitUnit: value })}
              disabled={readOnly}
              options={[
                { value: "minutes", label: "Minutes" },
                { value: "hours", label: "Hours" },
                { value: "days", label: "Days" },
              ]}
            />
          </Field>
        </div>
      )}

      {actionType === "http_post" && (
        <>
          <Field label="URL">
            <TextInput
              value={config.url ?? ""}
              onChange={(v) => updateConfig({ url: v })}
              placeholder="https://example.com/webhook"
              readOnly={readOnly}
              mono
            />
          </Field>
          <Field label="Body template (JSON)">
            <ExpandableHighlightedTextarea
              value={config.body ?? ""}
              onChange={(v) => updateConfig({ body: v })}
              placeholder='{"event": "new_lead"}'
              readOnly={readOnly}
              rows={4}
              variables={variables}
              title="Body template (JSON)"
            />
          </Field>
        </>
      )}

      {actionType === "send_email" && (
        <>
          <Field label="To">
            <ExpandableHighlightedTextarea
              value={config.to ?? ""}
              onChange={(v) => updateConfig({ to: v.replace(/\r?\n/g, " ") })}
              placeholder="recipient@example.com"
              readOnly={readOnly}
              rows={1}
              variables={variables}
              title="Email recipient"
              singleLine
              className="h-[58px]"
            />
          </Field>
          <Field label="CC (optional)">
            <ExpandableHighlightedTextarea
              value={config.cc ?? ""}
              onChange={(v) => updateConfig({ cc: v.replace(/\r?\n/g, " ") })}
              placeholder="reviewer@example.com"
              readOnly={readOnly}
              rows={1}
              variables={variables}
              title="CC recipients"
              singleLine
              className="h-[58px]"
            />
          </Field>
          <Field label="Subject">
            <ExpandableHighlightedTextarea
              value={config.subject ?? ""}
              onChange={(v) => updateConfig({ subject: v.replace(/\r?\n/g, " ") })}
              placeholder="Email subject"
              readOnly={readOnly}
              rows={1}
              variables={variables}
              title="Email subject"
              singleLine
              className="h-[58px]"
            />
          </Field>
          <Field label="Body">
            <ExpandableHighlightedTextarea
              value={config.body ?? ""}
              onChange={(v) => updateConfig({ body: v })}
              placeholder="Email body"
              readOnly={readOnly}
              rows={4}
              variables={variables}
              title="Email body"
            />
          </Field>
        </>
      )}

      {actionType === "wait_for_email_reply" && (
        <>
          {[
            ["messageId", "Message ID", "{{steps.e03.outputs.message_id}}"],
            ["conversationId", "Conversation ID", "{{steps.e03.outputs.conversation_id}}"],
            ["internetMessageId", "Internet message ID", "{{steps.e03.outputs.internet_message_id}}"],
            ["expectedFrom", "Expected sender", "{{steps.e03.outputs.sent_to}}"],
            ["sentAt", "Sent at", "{{steps.e03.outputs.email_sent_at}}"],
          ].map(([key, label, placeholder]) => (
            <Field key={key} label={label}>
              <ExpandableHighlightedTextarea
                value={config[key] ?? ""}
                onChange={(value) =>
                  updateConfig({ [key]: value.replace(/\r?\n/g, " ") })
                }
                placeholder={placeholder}
                readOnly={readOnly}
                rows={1}
                variables={variables}
                title={label}
                singleLine
                className="h-[58px]"
              />
            </Field>
          ))}
          {config.deadlineAt && !config.waitDuration && (
            <p className="text-[11px] text-amber-500">
              This saved workflow uses the previous exact deadline. Enter a
              duration above to replace it.
            </p>
          )}
          <Field label="Ignored message IDs (JSON)">
            <ExpandableHighlightedTextarea
              value={config.ignoredMessageIds ?? ""}
              onChange={(value) => updateConfig({ ignoredMessageIds: value })}
              placeholder='["{{steps.e04.outputs.reply_message_id}}"]'
              readOnly={readOnly}
              rows={3}
              variables={variables}
              title="Ignored message IDs"
            />
          </Field>
          <p className="text-[11px] text-text-muted">
            ProClaw calculates the deadline from the original sent time. The
            platform watches the mailbox and routes timeout as a normal result.
          </p>
        </>
      )}

      {actionType === "post_to_channel" && (
        <>
          <Field label="Channel ID">
            <TextInput
              value={config.channelId ?? ""}
              onChange={(v) => updateConfig({ channelId: v })}
              placeholder="uuid of the agent channel"
              readOnly={readOnly}
              mono
            />
          </Field>
          <Field label="Message">
            <ExpandableHighlightedTextarea
              value={config.message ?? ""}
              onChange={(v) => updateConfig({ message: v })}
              placeholder="Message to post in the channel"
              readOnly={readOnly}
              rows={3}
              variables={variables}
              title="Channel message"
            />
          </Field>
        </>
      )}

      {actionType === "mcp_tool" && (
        <>
          <Field label="Tool name">
            <TextInput
              value={config.tool ?? ""}
              onChange={(v) => updateConfig({ tool: v })}
              placeholder="e.g. proclaw-agent-comms.send_message"
              readOnly={readOnly}
              mono
            />
          </Field>
          <Field label="Arguments (JSON)">
            <ExpandableHighlightedTextarea
              value={argsDisplay(config)}
              onChange={(v) => {
                // Store a parsed object when valid (matches agent-authored
                // workflows); keep the raw text until it parses so the user
                // can type freely.
                let parsed: unknown = v;
                try {
                  parsed = JSON.parse(v);
                } catch {
                  /* keep raw string until valid JSON */
                }
                update({ config: { ...config, args: parsed } });
              }}
              placeholder='{"channel": "ops", "content": "{{steps.summarise.outputs.summary}}"}'
              readOnly={readOnly}
              rows={5}
              variables={variables}
              title="Arguments (JSON)"
            />
          </Field>
          <p className="text-[11px] text-text-muted">
            The executing agent must already have this tool. Args may
            reference prior step outputs via{" "}
            <code>{"{{steps.<id>.outputs.<key>}}"}</code>.
          </p>
        </>
      )}

      <Field label="Description">
        <Textarea
          value={(data.description as string) ?? ""}
          onChange={(v) => update({ description: v })}
          placeholder="Shown on the node body"
          readOnly={readOnly}
          rows={2}
        />
      </Field>
    </div>
  );
}
